import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button,
  Divider,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Avatar,
  AvatarGroup,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Stack,
  Link
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import EditNoticeModal from './EditNoticeModal';
import { useState } from 'react';
import DeleteConfirmationModal from './DeleteConfirmationModal';

// Utility function to convert URLs to clickable links
const convertUrlsToLinks = (text) => {
  if (!text) return '';
  
  // Simplified URL detection regex that prevents splitting protocols from domains
  const urlPattern = /(https?:\/\/\S+|www\.\S+|[a-z0-9][-a-z0-9.]+\.[a-z]{2,}(?:\/\S*)?)/gi;

  // Find all matches
  let lastIndex = 0;
  const result = [];
  
  // Create a regex object with the g flag to find all matches
  const regex = new RegExp(urlPattern);
  let match;
  
  // Manual text processing to properly handle all URL forms
  while ((match = regex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      result.push(text.substring(lastIndex, match.index));
    }
    
    // Get the matched URL
    const url = match[0];
    
    // Determine if the URL already has a protocol
    const hasProtocol = url.match(/^https?:\/\//i);
    
    // Prepare the href with proper protocol
    const href = hasProtocol ? url : 
                url.startsWith('www.') ? `https://${url}` : 
                `https://${url}`;
    
    // Add the link component
    result.push(
      <Link 
        key={match.index} 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        sx={{ wordBreak: 'break-word' }}
      >
        {url}
      </Link>
    );
    
    // Update last index
    lastIndex = match.index + url.length;
  }
  
  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    result.push(text.substring(lastIndex));
  }
  
  return result;
};

const NoticeList = ({ notices, currentUser, onMarkAsRead, onUpdateNotice, onDeleteNotice }) => {
  const { t } = useTranslation();
  const [editingNotice, setEditingNotice] = useState(null);
  const [deletingNotice, setDeletingNotice] = useState(null);

  if (!notices || notices.length === 0) {
    return (
      <Box my={4} textAlign="center">
        <Typography variant="body1" color="text.secondary">
          {t('common:messages.noData')}
        </Typography>
      </Box>
    );
  }

  const hasUserReadNotice = (notice) => {
    return notice.readBy.some(item => item.user._id === currentUser._id);
  };

  return (
    <Box>
      {notices.map((notice) => {
        const isAuthor = notice.author._id === currentUser._id;
        const isAdmin = currentUser.isAdmin;
        const canEdit = isAuthor || isAdmin;
        const userHasRead = hasUserReadNotice(notice);
        const readCount = notice.readBy.length;
        
        return (
          <Card 
            key={notice._id} 
            sx={{ 
              mb: 3, 
              position: 'relative',
              borderRadius: 2,
              boxShadow: 3,
              transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
              '&:hover': {
                boxShadow: 6,
                transform: 'translateY(-2px)'
              },
              borderLeft: userHasRead ? '4px solid' : 'none',
              borderColor: userHasRead ? 'primary.main' : 'transparent'
            }}
          >
            {userHasRead && (
              <Chip
                icon={<CheckCircleIcon />}
                label={t('common:status.read')}
                color="primary"
                size="small"
                sx={{ 
                  position: 'absolute', 
                  top: 10, 
                  right: 10,
                  boxShadow: 1
                }}
              />
            )}

            <CardHeader
              avatar={
                <Avatar 
                  alt={notice.author.name || notice.author.username} 
                  src={notice.author.imageUrl}
                  sx={{ 
                    width: 48, 
                    height: 48,
                    boxShadow: 2
                  }}
                />
              }
              title={
                <Typography variant="h6" fontWeight="medium">
                  {notice.title}
                </Typography>
              }
              subheader={
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {notice.author.name || notice.author.username} • {format(new Date(notice.createdAt), 'PPp')}
                  </Typography>
                </Box>
              }
              sx={{ pb: 0 }}
            />
            
            <CardContent sx={{ pt: 1 }}>
              <Typography 
                variant="body1" 
                component="div" 
                sx={{ 
                  whiteSpace: 'pre-wrap', 
                  mb: 3,
                  px: 1,
                  py: 1.5,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)', 
                  borderRadius: 1,
                  boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.05)'
                }}
              >
                {convertUrlsToLinks(notice.content)}
              </Typography>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box 
                display="flex" 
                alignItems="center" 
                justifyContent="space-between"
                flexWrap="wrap"
                gap={1}
              >
                {/* Left side: Reader avatars and count */}
                <Box display="flex" alignItems="center">
                  <VisibilityIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                  {readCount > 0 ? (
                    <>
                      <AvatarGroup 
                        max={8}
                        sx={{
                          '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            fontSize: '0.875rem',
                            border: '2px solid',
                            borderColor: theme => theme.palette.background.paper,
                            boxShadow: 1
                          }
                        }}
                      >
                        {notice.readBy.map((reader) => (
                          <Tooltip 
                            key={reader.user._id} 
                            title={reader.user.name || reader.user.username}
                            arrow
                            placement="top"
                          >
                            <Avatar 
                              alt={reader.user.name || reader.user.username} 
                              src={reader.user.imageUrl}
                            />
                          </Tooltip>
                        ))}
                      </AvatarGroup>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {readCount} {t('common:labels.readers')}
                      </Typography>
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      0 {t('common:labels.readers')}
                    </Typography>
                  )}
                </Box>
                
                {/* Right side: Action buttons */}
                <Stack direction="row" spacing={1} alignItems="center">
                  {!userHasRead && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => onMarkAsRead(notice._id)}
                      sx={{ 
                        borderRadius: 4,
                        px: 2,
                        boxShadow: 2
                      }}
                    >
                      {t('common:actions.markAsRead')}
                    </Button>
                  )}
                  
                  {canEdit && (
                    <>
                      <Tooltip title={t('common:actions.edit')}>
                        <IconButton 
                          color="primary" 
                          onClick={() => setEditingNotice(notice)}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('common:actions.delete')}>
                        <IconButton 
                          color="error" 
                          onClick={() => setDeletingNotice(notice)}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        );
      })}

      {editingNotice && (
        <EditNoticeModal
          open={Boolean(editingNotice)}
          notice={editingNotice}
          onClose={() => setEditingNotice(null)}
          onUpdateNotice={(data) => {
            onUpdateNotice(editingNotice._id, data);
            setEditingNotice(null);
          }}
        />
      )}

      {deletingNotice && (
        <DeleteConfirmationModal
          open={Boolean(deletingNotice)}
          onClose={() => setDeletingNotice(null)}
          onConfirm={() => {
            onDeleteNotice(deletingNotice._id);
            setDeletingNotice(null);
          }}
          title={t('common:dialogs.deleteTitle')}
          message={t('common:dialogs.deleteMessage')}
        />
      )}
    </Box>
  );
};

export default NoticeList; 